import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authorizedFetch } from '@/lib/api'

export interface Notice {
  id: string
  body: string
  audiences: string[]
  starts_at: string
  ends_at: string | null
  dismissible: boolean
  also_notify: boolean
  created_at: string
  updated_at: string
}

export interface NoticeWrite {
  body: string
  audiences: string[]
  starts_at: string
  ends_at: string | null
  dismissible: boolean
  also_notify: boolean
}

export const noticesQueryKey = ['notices'] as const
export const activeNoticeQueryKey = ['notices-active'] as const

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json()
    if (typeof data?.detail === 'string') return data.detail
    if (typeof data?.error === 'string') return data.error
  } catch {
    // The body was not JSON.
  }
  return fallback
}

export function useNotices() {
  return useQuery({
    queryKey: noticesQueryKey,
    queryFn: async (): Promise<Notice[]> => {
      const response = await authorizedFetch('/api/notices/')
      if (!response.ok) {
        throw new Error(await readError(response, 'Could not load notices.'))
      }
      return response.json()
    },
  })
}

export function useActiveNotice(enabled: boolean) {
  return useQuery({
    queryKey: activeNoticeQueryKey,
    enabled,
    queryFn: async (): Promise<Notice | null> => {
      const response = await authorizedFetch('/api/notices/active')
      if (!response.ok) return null
      const data = (await response.json()) as { notice: Notice | null }
      return data.notice ?? null
    },
  })
}

export function useSaveNotice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: NoticeWrite }) => {
      const response = await authorizedFetch(id ? `/api/notices/${id}` : '/api/notices/', {
        method: id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(id ? { ...payload, set_ends_at: true } : payload),
      })
      if (!response.ok) {
        throw new Error(await readError(response, 'Could not save the notice.'))
      }
      return response.json() as Promise<Notice>
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: noticesQueryKey })
      await queryClient.invalidateQueries({ queryKey: activeNoticeQueryKey })
    },
  })
}

export function useDismissNotice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await authorizedFetch(`/api/notices/${id}/dismiss`, { method: 'POST' })
      if (!response.ok) {
        throw new Error(await readError(response, 'Could not dismiss the notice.'))
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: activeNoticeQueryKey })
    },
  })
}
